"use client";

import React from "react";
import { Sigma, Box, Dices, Crosshair, Terminal, ShieldAlert, Leaf, HeartPulse, Briefcase, Landmark, Wheat, Cpu, Tv, GraduationCap, Users } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
const laboratories = [{
  id: "lab-01",
  title: "Agriculture",
  description: "Researching sustainable farming practices and food security policies to support rural development and nutrition.",
  icon: Wheat,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Rural Dev",
  threatLevel: "Critical",
  scanData: "Crop yield data integrated from 5 regions."
}, {
  id: "lab-02",
  title: "Environment, Energy, and Water",
  description: "Investigating climate resilience, renewable energy transitions, and equitable access to clean water resources.",
  icon: Leaf,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Eco-Systems",
  threatLevel: "Urgent",
  scanData: "Water table monitoring active."
}, {
  id: "lab-03",
  title: "Health and Social Safety",
  description: "Evaluating public health infrastructure and social safety nets to ensure comprehensive care for vulnerable populations.",
  icon: HeartPulse,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Public Health",
  threatLevel: "High",
  scanData: "Healthcare accessibility index updated."
}, {
  id: "lab-04",
  title: "Economics and Labor",
  description: "Analyzing market trends, employment rights, and labor policies to foster a fair and productive workforce.",
  icon: Briefcase,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Labor Rights",
  threatLevel: "Structural",
  scanData: "Unemployment trend analysis complete."
}, {
  id: "lab-05",
  title: "Business and Finance",
  description: "Examining corporate governance, financial regulations, and investment strategies for sustainable economic growth.",
  icon: Landmark,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Market Reg",
  threatLevel: "Moderate",
  scanData: "Financial transparency audit ongoing."
}, {
  id: "lab-06",
  title: "Technology",
  description: "Exploring the impact of artificial intelligence, digital privacy, and emerging tech on democratic institutions.",
  icon: Cpu,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Digital Policy",
  threatLevel: "Evolving",
  scanData: "AI governance framework under review."
}, {
  id: "lab-07",
  title: "Entertainment and Communications",
  description: "Studying media influence, information integrity, and the role of digital platforms in public discourse.",
  icon: Tv,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Media Ethics",
  threatLevel: "Systemic",
  scanData: "Misinformation tracking active."
}, {
  id: "lab-08",
  title: "Education",
  description: "Advocating for equitable access to quality learning and the modernization of educational infrastructure.",
  icon: GraduationCap,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Pedagogy",
  threatLevel: "Foundational",
  scanData: "Literacy rate projections updated."
}, {
  id: "lab-09",
  title: "Civic Society",
  description: "Strengthening non-governmental organizations and community-led initiatives to enhance democratic participation.",
  icon: Users,
  iconAnimation: "group-hover:scale-110 transition-transform duration-300",
  iconColor: "text-white",
  difficulty: "Community",
  threatLevel: "Vital",
  scanData: "NGO network expansion mapped."
}];
export default function SkillBuildingLaboratories() {
  return <section data-section-id="225264" id="skill-building-laboratories" className="relative w-full bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary to-transparent" />
        <div className="absolute top-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,var(--background)_100%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col gap-16">
        
        <div className="flex flex-col items-center text-center gap-4 transition-all duration-1000 transform opacity-100 translate-y-0">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-none bg-primary/5 border border-primary/20 text-primary font-mono text-xs tracking-widest uppercase mb-2">
            <Terminal className="size-4" />
            <span>Research Repository</span>
          </div>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-foreground">
            Knowledge <span className="text-primary">Hub</span>
          </h2>
          <p className="max-w-2xl text-muted-foreground font-default text-sm md:text-base">Explore our&nbsp; publications and data-driven insights. Empowering citizens through rigorous intellectual analysis and advocacy.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 transition-all duration-1000 delay-200 transform opacity-100 translate-y-0">
          {laboratories.map((lab, i) => <Card key={lab.id} data-index={i} className="group relative overflow-hidden bg-primary border-none rounded-none hover:bg-primary/90 transition-all duration-500 flex flex-col h-full">
              <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              
              <CardHeader className="relative z-10 pb-4">
                <div className="flex justify-between items-start mb-4">
                  <div className={`p-3 rounded-none bg-white/10 border border-white/20 ${lab.iconColor}`}>
                    <lab.icon className={`size-8 ${lab.iconAnimation}`} />
                  </div>
                  
                  <Badge variant="outline" className="opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-[-10px] group-hover:translate-y-0 bg-chart-2 text-primary-foreground border-transparent font-mono font-bold">
                    <Crosshair className="size-3 mr-1" />
                    {lab.difficulty}
                  </Badge>
                </div>

                <HoverCard>
                  <HoverCardTrigger asChild>
                    <div className="cursor-help inline-block">
                      <CardTitle className="font-default text-2xl font-bold tracking-tight text-white transition-colors">
                        {lab.title}
                      </CardTitle>
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 bg-card backdrop-blur-xl border-primary/30 font-mono text-sm rounded-none">
                    <div className="flex justify-between space-x-4">
                      <div className="space-y-1">
                        <h4 className="text-sm font-semibold text-primary flex items-center gap-2">
                          <ShieldAlert className="size-4" />
                          Data Insight
                        </h4>
                        <p className="text-muted-foreground">
                          {lab.scanData}
                        </p>
                        <div className="flex items-center pt-2">
                          <span className="text-xs text-muted-foreground">
                            Priority: <span className="text-primary font-bold">{lab.threatLevel}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </CardHeader>
              
              <CardContent className="relative z-10 flex-grow">
                <CardDescription className="font-default text-white/80 leading-relaxed">
                  {lab.description}
                </CardDescription>
              </CardContent>

              <CardFooter className="relative z-10 pt-4 border-t border-white/10 mt-auto">
                <Button variant="ghost" className="w-full font-mono uppercase tracking-wider text-xs text-white hover:bg-white/10 hover:text-white transition-colors rounded-none" onClick={() => window.location.href = i === 0 ? "/category/agriculture" : i === 1 ? "/category/environment-energy-and-water" : i === 2 ? "/category/health-and-social-safety" : i === 3 ? "/category/economics-and-labor" : i === 4 ? "/category/business-and-finance" : i === 5 ? "/category/technology" : i === 6 ? "/category/entertainment-and-communications" : i === 7 ? "/category/education" : i === 8 ? "/category/civic-society" : "/ResearchAndCommentaryPage"}>
                  Access Publication
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        <div className="flex justify-center mt-4">
          <Button variant="link" className="font-default text-primary font-semibold flex items-center gap-2 hover:gap-3 transition-all" onClick={() => window.location.href = "/Blog/"}>
            View All Research <span className="text-xl">→</span>
          </Button>
        </div>

      </div>
    </section>;
}